/** @jsxImportSource @emotion/react */
import { useMemo, useState } from "react";
import { css } from "@emotion/react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    TooltipContentProps,
    XAxis,
    YAxis,
} from "recharts";
import { Text } from "@phoenix/components";
import {
    ChartTooltip,
    ChartTooltipItem,
    defaultCartesianGridProps,
    defaultXAxisProps,
    defaultYAxisProps,
    useCategoryChartColors,
} from "@phoenix/components/chart";

export type ChartType =
    | "vertical_bar"
    | "horizontal_bar"
    | "line"
    | "pie"
    | "big_number";

export interface ChartDataPoint {
    label: string;
    value: number;
}

interface Props {
    data: ChartDataPoint[];
    chartType: ChartType;
    valueFormatter?: (v: number) => string;
    colorPalette?: string[];
    emptyIcon?: string;
    emptyText?: string;
}

const DEFAULT_COLORS = [
    "#6366f1", "#818cf8", "#a78bfa", "#7c3aed", "#5b21b6",
    "#4c1d95", "#c4b5fd", "#ddd6fe", "#8b5cf6", "#ede9fe",
];

function fmt(v: number, formatter?: (v: number) => string): string {
    if (formatter) return formatter(v);
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return v.toLocaleString();
}

function EmptyState({ icon, text }: { icon?: string; text?: string }) {
    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            flexDirection: "column",
            gap: 8,
            color: "var(--ac-global-text-color-500)",
            fontSize: 13,
        }}>
            {icon && <span style={{ fontSize: 28, opacity: 0.3 }}>{icon}</span>}
            <span>{text ?? "No data available"}</span>
        </div>
    );
}

// ── Shared tooltip ─────────────────────────────────────────────────────────────
function ChartTooltipContent({
    active,
    payload,
    label,
    valueFormatter,
    color,
}: TooltipContentProps<number, string> & { valueFormatter?: (v: number) => string; color: string }) {
    if (active && payload && payload.length) {
        return (
            <ChartTooltip>
                {label && <Text weight="heavy" size="S">{label}</Text>}
                <ChartTooltipItem
                    color={color}
                    shape="circle"
                    name="Value"
                    value={fmt(payload[0]?.value ?? 0, valueFormatter)}
                />
            </ChartTooltip>
        );
    }
    return null;
}

// ── Vertical Bar ───────────────────────────────────────────────────────────────
function VerticalBarChart({ data, colors, formatter }: { data: ChartDataPoint[]; colors: string[]; formatter?: (v: number) => string }) {
    const chartData = data.map((d) => ({ ...d, name: d.label }));
    const safeColors = colors.length > 0 ? colors : DEFAULT_COLORS;
    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 45 }}>
                <CartesianGrid {...defaultCartesianGridProps} vertical={false} />
                <XAxis {...defaultXAxisProps} dataKey="name" type="category" angle={-25} textAnchor="end" interval={0} tick={{ fontSize: 10 }} height={50} />
                <YAxis {...defaultYAxisProps} tickFormatter={(v) => fmt(v, formatter)} width={65} tick={{ fontSize: 10 }} />
                <Tooltip
                    content={(props) => <ChartTooltipContent {...props} valueFormatter={formatter} color={safeColors[0]} />}
                    cursor={{ fill: "var(--chart-tooltip-cursor-fill-color)", opacity: 0.4 }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} fill={safeColors[0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={safeColors[i % safeColors.length]} />)}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

// ── Horizontal Bar ─────────────────────────────────────────────────────────────
function HorizontalBarChart({ data, colors, formatter }: { data: ChartDataPoint[]; colors: string[]; formatter?: (v: number) => string }) {
    const chartData = [...data].reverse().map((d) => ({ ...d, name: d.label }));
    const safeColors = colors.length > 0 ? colors : DEFAULT_COLORS;
    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid {...defaultCartesianGridProps} horizontal={false} />
                <XAxis {...defaultXAxisProps} type="number" tickFormatter={(v) => fmt(v, formatter)} tick={{ fontSize: 10 }} />
                <YAxis {...defaultYAxisProps} dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                <Tooltip
                    content={(props) => <ChartTooltipContent {...props} valueFormatter={formatter} color={safeColors[0]} />}
                    cursor={{ fill: "var(--chart-tooltip-cursor-fill-color)", opacity: 0.4 }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} fill={safeColors[0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={safeColors[i % safeColors.length]} />)}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

// ── Line Chart ─────────────────────────────────────────────────────────────────
function LineChartView({ data, colors, formatter }: { data: ChartDataPoint[]; colors: string[]; formatter?: (v: number) => string }) {
    const chartData = data.map((d) => ({ name: d.label, value: d.value }));
    const lineColor = colors[0] ?? "#6366f1";
    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                <CartesianGrid {...defaultCartesianGridProps} vertical={false} />
                <XAxis
                    {...defaultXAxisProps}
                    dataKey="name"
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                    tick={{ fontSize: 11 }}
                />
                <YAxis
                    {...defaultYAxisProps}
                    tickFormatter={(v) => fmt(v, formatter)}
                    width={60}
                />
                <Tooltip
                    content={(props) => <ChartTooltipContent {...props} valueFormatter={formatter} color={lineColor} />}
                    cursor={{ stroke: lineColor, strokeWidth: 1, strokeDasharray: "4 4" }}
                />
                <Line
                    type="monotone"
                    dataKey="value"
                    stroke={lineColor}
                    strokeWidth={2.5}
                    dot={{ fill: "var(--ac-global-background-color-dark)", stroke: lineColor, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: lineColor, stroke: "var(--ac-global-background-color-dark)", strokeWidth: 2 }}
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

// ── Pie Chart ──────────────────────────────────────────────────────────────────
function PieChartView({ data, colors, formatter }: { data: ChartDataPoint[]; colors: string[]; formatter?: (v: number) => string }) {
    const chartData = data.map((d) => ({ name: d.label, value: d.value }));
    return (
        <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="45%"
                    outerRadius="65%"
                    innerRadius="35%"
                    paddingAngle={2}
                >
                    {chartData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                </Pie>
                <Tooltip formatter={(v: number) => fmt(v, formatter)} />
                <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
        </ResponsiveContainer>
    );
}

// ── Big Number with dropdown ───────────────────────────────────────────────────
const selectCSS = css`
  margin-top: 16px;
  background: var(--ac-global-color-grey-100);
  border: 1px solid var(--ac-global-color-grey-300);
  border-radius: 8px;
  color: var(--ac-global-text-color-900);
  font-size: 13px;
  padding: 7px 32px 7px 12px;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3e%3cpath d='M6 9l6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 14px;
  min-width: 160px;
  max-width: 220px;
  text-overflow: ellipsis;
  transition: border-color 0.15s;
  &:hover, &:focus {
    border-color: var(--ac-global-color-primary);
  }
`;

const bigValueCSS = css`
  font-size: 38px;
  font-weight: 800;
  color: var(--ac-global-text-color-900);
  line-height: 1;
  letter-spacing: -1px;
`;

const bigLabelCSS = css`
  font-size: 11px;
  color: var(--ac-global-text-color-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 6px;
`;

const bigSubValueCSS = css`
  font-size: 22px;
  font-weight: 700;
  color: var(--ac-global-color-primary);
  margin-top: 4px;
`;

function BigNumberView({ data, formatter }: { data: ChartDataPoint[]; formatter?: (v: number) => string }) {
    const [selectedLabel, setSelectedLabel] = useState<string>("__total__");

    const total = useMemo(() => data.reduce((sum, d) => sum + d.value, 0), [data]);

    const selectedItem = useMemo(() => {
        if (selectedLabel === "__total__") return null;
        return data.find((d) => d.label === selectedLabel) ?? null;
    }, [selectedLabel, data]);

    const displayValue = selectedItem != null ? selectedItem.value : total;
    const displayLabel = selectedItem != null ? selectedItem.label : "Total — all items";

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: 4,
        }}>
            {/* Big primary number */}
            <div css={bigValueCSS}>{fmt(displayValue, formatter)}</div>
            <div css={bigLabelCSS}>{displayLabel}</div>

            {/* If a specific item is selected, also show % of total */}
            {selectedItem && (
                <div css={bigSubValueCSS}>
                    {total > 0 ? `${((selectedItem.value / total) * 100).toFixed(1)}% of total` : ""}
                </div>
            )}

            {/* Dropdown to pick item */}
            <select
                css={selectCSS}
                value={selectedLabel}
                onChange={(e) => setSelectedLabel(e.target.value)}
            >
                <option value="__total__">📊 All items (total)</option>
                <optgroup label="─────────────────">
                    {data.map((d) => (
                        <option key={d.label} value={d.label}>
                            {d.label} — {fmt(d.value, formatter)}
                        </option>
                    ))}
                </optgroup>
            </select>
        </div>
    );
}

// ── Main Renderer ──────────────────────────────────────────────────────────────
export function ChartTypeRenderer({
    data,
    chartType,
    valueFormatter,
    colorPalette = DEFAULT_COLORS,
    emptyIcon,
    emptyText,
}: Props) {
    if (!data || data.length === 0) {
        return <EmptyState icon={emptyIcon} text={emptyText} />;
    }

    switch (chartType) {
        case "vertical_bar":
            return <VerticalBarChart data={data} colors={colorPalette} formatter={valueFormatter} />;
        case "horizontal_bar":
            return <HorizontalBarChart data={data} colors={colorPalette} formatter={valueFormatter} />;
        case "line":
            return <LineChartView data={data} colors={colorPalette} formatter={valueFormatter} />;
        case "pie":
            return <PieChartView data={data} colors={colorPalette} formatter={valueFormatter} />;
        case "big_number":
            return <BigNumberView data={data} formatter={valueFormatter} />;
        default:
            return <VerticalBarChart data={data} colors={colorPalette} formatter={valueFormatter} />;
    }
}
